/**
 * 任务调度相关API
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';

// 任务数据类型定义
export interface JobItem {
  id: string;
  jobName: string;        // 任务名称
  jobGroup: string;       // 任务组
  invokeTarget: string;   // 调用目标
  cronExpression: string; // cron表达式
  status: 'running' | 'paused'; // 状态：运行中、暂停
  concurrent: boolean;    // 是否允许并发执行
  misfirePolicy: 'default' | 'ignore' | 'fire_once'; // 计划执行错误策略
  description?: string;   // 描述信息
  createTime: string;     // 创建时间
  updateTime: string;     // 更新时间
}

// 任务执行日志
export interface JobLogItem {
  id: string;
  jobId: string;         // 任务ID
  jobName: string;       // 任务名称
  jobGroup: string;      // 任务组名
  invokeTarget: string;  // 调用目标
  status: 'success' | 'fail'; // 执行状态
  exceptionInfo?: string; // 异常信息
  startTime: string;     // 开始时间
  endTime: string;       // 结束时间
  duration: number;      // 执行耗时(毫秒)
  createTime: string;    // 创建时间
}

export interface JobFormData {
  jobName: string;
  jobGroup: string;
  invokeTarget: string;
  cronExpression: string;
  concurrent: boolean;
  misfirePolicy: 'default' | 'ignore' | 'fire_once';
  status: 'running' | 'paused';
  description?: string;
}

export interface JobListParams {
  jobName?: string;
  jobGroup?: string;
  status?: 'running' | 'paused';
  page?: number;
  limit?: number;
}

export interface JobLogListParams {
  jobId?: string;
  jobName?: string;
  status?: 'success' | 'fail';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

// API接口函数

/**
 * 获取任务列表
 */
export function getJobList(params?: JobListParams) {
  return handleApiResponse(unifiedApi.service('job').get<{ data: JobItem[]; count: number }>('/job', { params }));
}

/**
 * 获取任务详情
 */
export function getJobById(id: string) {
  return handleApiResponse(unifiedApi.service('job').get<{ data: JobItem; count: number }>(`/job/${id}`));
}

/**
 * 创建任务
 */
export function createJob(data: JobFormData) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: JobItem; count: number }>('/job', data));
}

/**
 * 更新任务
 */
export function updateJob(id: string, data: JobFormData) {
  return handleApiResponse(unifiedApi.service('job').put<{ data: JobItem; count: number }>(`/job/${id}`, data));
}

/**
 * 删除任务
 */
export function deleteJob(id: string) {
  return handleApiResponse(unifiedApi.service('job').delete<{ data: null; count: number }>(`/job/${id}`));
}

/**
 * 批量删除任务
 */
export function batchDeleteJobs(ids: string[]) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: null; count: number }>('/job/batch', { ids }));
}

/**
 * 执行任务
 */
export function runJob(id: string) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: null; count: number }>(`/job/${id}/run`));
}

/**
 * 暂停任务
 */
export function pauseJob(id: string) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: JobItem; count: number }>(`/job/${id}/pause`));
}

/**
 * 恢复任务
 */
export function resumeJob(id: string) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: JobItem; count: number }>(`/job/${id}/resume`));
}

/**
 * 获取任务日志列表
 */
export function getJobLogList(params?: JobLogListParams) {
  return handleApiResponse(unifiedApi.service('job').get<{ data: JobLogItem[]; count: number }>('/job/log', { params }));
}

/**
 * 获取任务日志详情
 */
export function getJobLogById(id: string) {
  return handleApiResponse(unifiedApi.service('job').get<{ data: JobLogItem; count: number }>(`/job/log/${id}`));
}

/**
 * 清空任务日志
 */
export function clearJobLogs() {
  return handleApiResponse(unifiedApi.service('job').delete<{ data: null; count: number }>('/job/log/clear'));
}

/**
 * 删除任务日志
 */
export function deleteJobLog(id: string) {
  return handleApiResponse(unifiedApi.service('job').delete<{ data: null; count: number }>(`/job/log/${id}`));
}

/**
 * 批量删除任务日志
 */
export function batchDeleteJobLogs(ids: string[]) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: null; count: number }>('/job/log/batch', { ids }));
}

/**
 * 校验cron表达式是否有效
 */
export function validateCronExpression(expression: string) {
  return handleApiResponse(unifiedApi.service('job').post<{ data: boolean; count: number }>('/job/validate-cron', { expression }));
}
